import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronsUpDown, Check, Plus, Loader2, AlertTriangle } from "lucide-react";
import { titleCase } from "@/lib/text";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Small Levenshtein — enough to catch typos (Frieghtliner vs Freightliner) at the source.
function editDistance(a, b) {
  a = (a || "").toLowerCase(); b = (b || "").toLowerCase();
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
}

// Find an exact (normalized) match + a ranked list of near-matches for the "did you mean" nudge.
export function findSimilar(query, options) {
  const q = (query || "").trim();
  if (!q) return { exact: null, near: [] };
  const nq = norm(q);
  let exact = null;
  const near = [];
  for (const o of options) {
    const no = norm(o.label);
    if (no === nq) { exact = o; continue; }
    const d = editDistance(nq, no);
    const maxlen = Math.max(nq.length, no.length) || 1;
    const ratio = 1 - d / maxlen;
    if ((no && nq && (no.includes(nq) || nq.includes(no))) || ratio >= 0.72 || d <= 2) {
      near.push({ ...o, _ratio: ratio });
    }
  }
  near.sort((a, b) => b._ratio - a._ratio);
  return { exact, near: near.slice(0, 6) };
}

// ONE consistent Identify field: searchable pick list + a DELIBERATE "+ New" flow that
// prompts + shows similar existing entries BEFORE creating, to stop dupes at the source.
export function IdentifyPickField({
  label, required, testid, value, options = [], onChange, onAddNew,
  addNoun = "entry", placeholder = "—", canAddNew = true, disabled = false,
  headerExtra = null, addGuard = null, addHint = null, field = null, onNearMiss = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const current = options.find((o) => o.value === value);
  const display = current ? current.label : (value || "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? options.filter((o) => (o.label || "").toLowerCase().includes(q)) : options;
    return base.slice(0, 300);
  }, [options, query]);

  const sim = useMemo(() => findSimilar(draft, options), [draft, options]);

  const openAdd = (seed = "") => {
    const guardMsg = addGuard ? addGuard() : null;
    if (guardMsg) { toast.error(guardMsg); return; }
    setDraft(seed); setAddOpen(true); setOpen(false);
  };

  const pick = (o) => { onChange(o.value); setOpen(false); setAddOpen(false); setQuery(""); };

  // Picked an existing entry FROM the add-dialog (a "did you mean" chip or the exact-match
  // "Use it") — this is a real near-miss signal, logged for the future merge admin.
  const pickSuggested = (o, resolution) => {
    if (onNearMiss && field) onNearMiss({ field, typed: draft.trim(), matched: sim.near.map((n) => n.label), chosen: o.label, resolution });
    pick(o);
  };

  const confirmAdd = async () => {
    const name = draft.trim();
    if (!name) return;
    if (sim.exact) { pickSuggested(sim.exact, "used_exact"); return; }
    const guardMsg = addGuard ? addGuard() : null;
    if (guardMsg) { toast.error(guardMsg); return; }
    setBusy(true);
    try {
      const newVal = await onAddNew(name);
      onChange(newVal);
      if (onNearMiss && field && sim.near.length) onNearMiss({ field, typed: name, matched: sim.near.map((n) => n.label), chosen: name, resolution: "created_new_despite_similar" });
      setAddOpen(false); setQuery("");
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e?.message || "Couldn't add.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between min-h-[20px]">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}{required && <span className="text-[#B01E28]"> *</span>}</Label>
          {headerExtra}
        </div>
        {canAddNew && (
          <button type="button" data-testid={`${testid}-new`} onClick={() => openAdd(query)}
            className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#B01E28] hover:underline">
            <Plus className="w-3 h-3" />New
          </button>
        )}
      </div>

      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testid}
            className="w-full justify-between rounded-lg border-slate-200 h-11 font-normal text-slate-900 mt-1.5">
            <span className={display ? "truncate" : "text-slate-400 truncate"}>{display ? titleCase(display) : placeholder}</span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 rounded-lg w-[--radix-popover-trigger-width] min-w-[220px]" align="start">
          <Command shouldFilter={false}>
            <CommandInput data-testid={`${testid}-input`} placeholder="Search…" value={query} onValueChange={setQuery} />
            <CommandList>
              {filtered.length === 0 && <CommandEmpty>No matches.</CommandEmpty>}
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o.value} value={o.label} onSelect={() => pick(o)} data-testid={`${testid}-opt-${o.value}`}>
                    <Check className={`w-4 h-4 mr-2 ${value === o.value ? "opacity-100 text-emerald-600" : "opacity-0"}`} />
                    {titleCase(o.label)}
                  </CommandItem>
                ))}
              </CommandGroup>
              {canAddNew && (
                <div className="border-t border-slate-100 p-1">
                  <button type="button" data-testid={`${testid}-new-inline`} onClick={() => openAdd(query)}
                    className="w-full flex items-center gap-2 text-sm text-[#B01E28] font-semibold px-2 py-2 rounded hover:bg-red-50">
                    <Plus className="w-4 h-4" />Add a new {addNoun}…
                  </button>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* DELIBERATE add — prompt + "did you mean" similarity to prevent dupes at the source */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent data-testid={`${testid}-add-dialog`} className="rounded-xl border-slate-200 max-w-md">
          <DialogTitle>Add a new {addNoun}</DialogTitle>
          {addHint && <p className="text-xs text-slate-500 -mt-2">{addHint}</p>}
          <Input data-testid={`${testid}-add-input`} autoFocus value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !sim.exact) confirmAdd(); }}
            placeholder={`New ${addNoun} name`} className="rounded-lg h-11" />

          {sim.exact && (
            <div data-testid={`${testid}-exact-warn`} className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>"<b>{titleCase(sim.exact.label)}</b>" already exists. Use it instead of creating a duplicate.
                <button type="button" data-testid={`${testid}-use-exact`} onClick={() => pickSuggested(sim.exact, "used_exact")} className="ml-2 font-semibold underline">Use it</button>
              </div>
            </div>
          )}

          {!sim.exact && sim.near.length > 0 && (
            <div data-testid={`${testid}-similar`} className="rounded-lg border border-slate-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Did you mean one of these?</div>
              <div className="flex flex-wrap gap-1.5">
                {sim.near.map((o) => (
                  <button key={o.value} type="button" data-testid={`${testid}-similar-${o.value}`} onClick={() => pickSuggested(o, "picked_existing")}
                    className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#E0781A] hover:text-[#B45309]">
                    {titleCase(o.label)}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Pick an existing one above, or confirm you really want a new entry.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-1">
            <Button variant="outline" className="rounded-lg" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid={`${testid}-add-confirm`} disabled={busy || !draft.trim() || !!sim.exact} onClick={confirmAdd}
              className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (sim.near.length > 0 ? `Add "${draft.trim()}" anyway` : `Add "${draft.trim()}"`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
